import {Fragment} from 'react';
import {List, ListItem, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {CarePlanActivityTypesArrangement} from '../../../backend/enums/care-plan-activity-types-arrangement';
import {CarePlanConfigurationFieldLayoutConfigurations} from '../../../backend/interfaces/care-plan-configuration-layout';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {CarePlanConfigurationFeature} from '../../../backend/enums/care-plan-configuration-feature';
import {RenderPreviewField} from './render-preview-field';
import {ConfigurationValueType, ConfigurationsIndex} from '../../../types';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {translations} from '../../../i18n/translations';

interface LinesOrColumnsCardContentProps {
  layoutType: CarePlanActivityTypesArrangement;
  configurationsIndex: ConfigurationsIndex | undefined;
  valuesIndex: {[key: string]: CarePlanActivitiesGroupApiRequestConfigurationsItem};
  activityType: ActivityType | undefined;
  nameConfigurationInfo: ConfigurationValueType<string>;
  activationStateConfigurationInfo: ConfigurationValueType<boolean>;
}

const LinesOrColumnsCardContent = ({
  layoutType,
  configurationsIndex,
  valuesIndex,
  activityType,
  nameConfigurationInfo,
  activationStateConfigurationInfo,
}: LinesOrColumnsCardContentProps) => {
  const {t} = useTranslation();

  const shouldRender = (
    fieldLayoutConfigurations: CarePlanConfigurationFieldLayoutConfigurations,
    fieldConfig: CarePlanConfiguration
  ) => {
    // Assume backend value
    let preview = fieldLayoutConfigurations.preview || false;

    // Override for displaying task name - in case the backend does not have it set
    if (
      fieldConfig?.feature === CarePlanConfigurationFeature.Name &&
      layoutType === CarePlanActivityTypesArrangement.Columns
    )
      preview = true;

    // When task is inactive, we hide all fields except the taks name
    if (
      activationStateConfigurationInfo?.value === false &&
      fieldConfig?.feature !== CarePlanConfigurationFeature.Name
    )
      preview = false;

    return preview;
  };

  const getMaxWidth = (previewWidthPercentage?: string | number) => {
    let maxWidth = 'unset';

    if (layoutType === CarePlanActivityTypesArrangement.Lines) {
      maxWidth = previewWidthPercentage ? `${previewWidthPercentage}%` : '450px';
    }

    return maxWidth;
  };

  return (
    <Stack
      style={{
        padding: '0 10px 0 0px',
        width: '100%',
      }}
    >
      {layoutType === CarePlanActivityTypesArrangement.Lines && (
        <Typography className="activity-type-info-value-label-lines" noWrap>
          {nameConfigurationInfo?.value || activityType?.name}
        </Typography>
      )}

      <List
        sx={{
          display: 'flex',
          gap: layoutType === CarePlanActivityTypesArrangement.Columns ? '2px' : 'unset',
          flexDirection: layoutType === CarePlanActivityTypesArrangement.Columns ? 'column' : 'row',
          justifyContent: 'space-between',
        }}
      >
        {configurationsIndex &&
          activityType?.carePlanConfigurationLayout &&
          activityType.carePlanConfigurationLayout?.configurations &&
          activityType.carePlanConfigurationLayout.configurations.map(
            (fieldLayoutConfigurations: CarePlanConfigurationFieldLayoutConfigurations, index) => {
              const field: CarePlanConfiguration =
                configurationsIndex[fieldLayoutConfigurations.carePlanConfigurationId];

              const value =
                valuesIndex[fieldLayoutConfigurations.carePlanConfigurationId]
                  ?.carePlanConfigurationValue;

              const maxWidth = getMaxWidth(
                CarePlanActivityTypesArrangement.Columns
                  ? fieldLayoutConfigurations?.previewWidthPercentage
                  : undefined
              );

              const preview = shouldRender(fieldLayoutConfigurations, field);

              if (preview) {
                return (
                  <ListItem
                    key={`field_${field.id}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: 0,
                      maxWidth,
                      width:
                        layoutType === CarePlanActivityTypesArrangement.Lines && index === 3
                          ? '220px'
                          : layoutType === CarePlanActivityTypesArrangement.Lines
                            ? '110px'
                            : 'unset',
                      minWidth:
                        layoutType === CarePlanActivityTypesArrangement.Lines && index === 3
                          ? '150px'
                          : layoutType === CarePlanActivityTypesArrangement.Lines
                            ? '100px'
                            : 'unset',
                    }}
                  >
                    {layoutType === CarePlanActivityTypesArrangement.Lines && (
                      <Typography className="activity-type-info-name-label" noWrap>
                        {field.name}
                      </Typography>
                    )}

                    <Typography
                      className={`activity-type-info-value-${
                        layoutType === CarePlanActivityTypesArrangement.Columns &&
                        field.feature === CarePlanConfigurationFeature.Name
                          ? 'label-columns'
                          : layoutType
                      }`}
                      sx={{
                        width: '100%',
                      }}
                      noWrap
                    >
                      <RenderPreviewField
                        {...{
                          activityType,
                          value,
                          field,
                          transport:
                            activityType.name ===
                            t(
                              translations.care_plan_sheet_activities_group_entry_configurationvalue
                                .aivd.transportation
                            ),
                        }}
                      />
                    </Typography>
                  </ListItem>
                );
              }

              return <Fragment key={`field_${field.id}`} />;
            }
          )}
      </List>
    </Stack>
  );
};

export default LinesOrColumnsCardContent;
